import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './InfoTip.css';

/**
 * The inline "i" — a clickable info mark that opens a small centred dialog.
 *
 * Cloned from `views/partials/info-tip.ejs`, which two views include
 * (`platforms/fit-criteria.ejs` and `opportunities-form.ejs`). It is a shared partial in
 * the source, so a shared component here is the faithful equivalent, not a refactor.
 *
 * THIS IS INVENTORY REGISTRY'S OWN COPY. AI Readiness carries the same file; §1.7 forbids
 * one product importing from another's folder, and APR already holds its own copy for the
 * same reason (`apr/compat/InfoTip.tsx`). Promoting it to ui-kit is a deliberate step for
 * a later session, not a side effect of adding two tips (2026-09-21).
 *
 * ── WHY IT RENDERS THROUGH A PORTAL ────────────────────────────────────────────────────
 *
 * The source relocates the overlay and dialog to `<body>` on open, and says why: a
 * `position: fixed` element is positioned against the nearest transformed, sticky or
 * overflow-clipped ancestor rather than the viewport, so left in place it gets trapped and
 * clipped — its example is a sticky table header inside a scroller. React's portal is the
 * same fix, applied at render rather than on click.
 *
 * Escape closes it, as it does in the source.
 */
export interface InfoTipProps {
  /** Dialog heading. */
  title: string;
  /** Dialog body. */
  children: ReactNode;
  /** Optional text beside the "i". */
  label?: string;
}

export function InfoTip({ title, children, label }: InfoTipProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="itip-btn"
        onClick={() => setOpen(true)}
        aria-label={`More info: ${title}`}
      >
        <span className="itip-i">i</span>
        {label ? <span className="itip-label">{label}</span> : null}
      </button>

      {open
        ? createPortal(
            <>
              <div className="itip-overlay on" onClick={() => setOpen(false)} />
              <aside className="itip-drawer on" role="dialog" aria-label={title}>
                <div className="itip-head">
                  <div className="itip-title">
                    <span className="itip-head-i">i</span>
                    {title}
                  </div>
                  <button
                    type="button"
                    className="itip-x"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="itip-body">{children}</div>
              </aside>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
